// 统一的图片压缩和上传工具
// 用于在所有图片上传入口统一处理图片压缩和保存

import { UPLOAD_CONFIG } from './uploadConfig';
import { normalizeUploadFileName, resolveUploadMime, validateIpeUploadFile } from './ipeImageUploadFormats';
import { mapWithUploadConcurrency, UPLOAD_COMPRESS_CONCURRENCY } from './globalUploadBudget';
import { assertUploadSessionLimit } from './uploadSessionLimits';

/**
 * 检测图片中的非空白区域（有内容的部分）
 * @param {ImageData} imageData - 图片的像素数据
 * @returns {{ x: number, y: number, width: number, height: number }} 裁剪区域
 */
function detectContentBounds(imageData) {
  const width = imageData.width;
  const height = imageData.height;
  const data = imageData.data;
  
  const alphaThreshold = 5; // alpha通道阈值（大于此值认为有内容）
  const rgbThreshold = 250; // RGB值阈值（用于检测边缘空白）
  
  // 先检查图片是否主要是透明背景（通过采样判断）
  let transparentPixelCount = 0;
  let opaquePixelCount = 0;
  const sampleSize = Math.min(100, width * height);
  const step = Math.max(1, Math.floor((width * height) / sampleSize));
  
  for (let i = 0; i < width * height; i += step) {
    const index = i * 4;
    const a = data[index + 3];
    if (a < alphaThreshold) {
      transparentPixelCount++;
    } else if (a > 200) {
      opaquePixelCount++;
    }
  }
  
  const isTransparentBackground = transparentPixelCount > opaquePixelCount;
  
  // 从边缘向内检测空白区域，而不是检测所有像素
  // 这样可以避免裁剪掉图片中间的白色内容
  let minX = 0;
  let minY = 0;
  let maxX = width - 1;
  let maxY = height - 1;
  
  // 检测函数：判断一个像素是否是空白
  const isBlankPixel = (x, y) => {
    const index = (y * width + x) * 4;
    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const a = data[index + 3];
    
    if (isTransparentBackground) {
      // 透明背景：alpha值小于阈值认为是空白
      return a < alphaThreshold;
    } else {
      // 不透明背景：RGB值都接近255（白色）且alpha值足够，认为是边缘空白
      // 注意：只用于检测边缘，不用于检测图片中间的白色内容
      return a > alphaThreshold && r >= rgbThreshold && g >= rgbThreshold && b >= rgbThreshold;
    }
  };
  
  // 从左边检测空白
  let foundLeft = false;
  for (let x = 0; x < width; x++) {
    let allBlank = true;
    for (let y = 0; y < height; y++) {
      if (!isBlankPixel(x, y)) {
        allBlank = false;
        break;
      }
    }
    if (!allBlank) {
      minX = x;
      foundLeft = true;
      break;
    }
  }
  
  // 从右边检测空白
  let foundRight = false;
  for (let x = width - 1; x >= minX; x--) {
    let allBlank = true;
    for (let y = 0; y < height; y++) {
      if (!isBlankPixel(x, y)) {
        allBlank = false;
        break;
      }
    }
    if (!allBlank) {
      maxX = x;
      foundRight = true;
      break;
    }
  }
  
  // 从上边检测空白
  let foundTop = false;
  for (let y = 0; y < height; y++) {
    let allBlank = true;
    for (let x = minX; x <= maxX; x++) {
      if (!isBlankPixel(x, y)) {
        allBlank = false;
        break;
      }
    }
    if (!allBlank) {
      minY = y;
      foundTop = true;
      break;
    }
  }
  
  // 从下边检测空白
  let foundBottom = false;
  for (let y = height - 1; y >= minY; y--) {
    let allBlank = true;
    for (let x = minX; x <= maxX; x++) {
      if (!isBlankPixel(x, y)) {
        allBlank = false;
        break;
      }
    }
    if (!allBlank) {
      maxY = y;
      foundBottom = true;
      break;
    }
  }
  
  // 如果所有边缘都是空白，返回整个图片区域（避免裁剪掉所有内容）
  if (!foundLeft && !foundRight && !foundTop && !foundBottom) {
    return { x: 0, y: 0, width, height };
  }
  
  // 添加一些边距（避免裁剪太紧，保留一些边缘）
  const padding = 2;
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(width - 1, maxX + padding);
  maxY = Math.min(height - 1, maxY + padding);
  
  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

/**
 * 裁剪图片，移除空白区域
 * @param {string} imageUrl - 图片的URL（可以是data URL、blob URL等）
 * @returns {Promise<string>} 裁剪后的图片data URL
 */
export const cropImageToContent = async (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // 创建临时canvas来检测内容区域
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = img.naturalWidth;
        tempCanvas.height = img.naturalHeight;
        
        // 绘制图片到临时canvas
        tempCtx.drawImage(img, 0, 0);
        
        // 获取图片数据
        const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        
        // 检测内容边界
        const bounds = detectContentBounds(imageData);
        
        // 如果内容区域就是整个图片，直接返回原图
        if (bounds.x === 0 && bounds.y === 0 && 
            bounds.width === img.naturalWidth && bounds.height === img.naturalHeight) {
          resolve(imageUrl);
          return;
        }
        
        // 创建裁剪后的canvas
        const croppedCanvas = document.createElement('canvas');
        const croppedCtx = croppedCanvas.getContext('2d');
        croppedCanvas.width = bounds.width;
        croppedCanvas.height = bounds.height;
        
        // 绘制裁剪后的图片
        croppedCtx.drawImage(
          img,
          bounds.x, bounds.y, bounds.width, bounds.height,
          0, 0, bounds.width, bounds.height
        );
        
        // 转换为data URL
        croppedCanvas.toBlob((blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('裁剪图片转换失败'));
            reader.readAsDataURL(blob);
          } else {
            reject(new Error('裁剪图片失败'));
          }
        }, 'image/png'); // 使用PNG格式以保持透明度
      } catch (error) {
        console.warn('裁剪图片失败，使用原图:', error);
        resolve(imageUrl); // 如果裁剪失败，返回原图
      }
    };
    img.onerror = () => {
      reject(new Error('加载图片失败'));
    };
    img.src = imageUrl;
  });
};

// 图片压缩配置（与 uploadConfig / .env 同步）

const MAX_DATABASE_DIMENSION = UPLOAD_CONFIG.maxEdgePx;
const DATABASE_IMAGE_QUALITY = UPLOAD_CONFIG.jpegQuality;
/** 用户上传硬上限（默认 50MB）；超过才拦截，以下走温和压缩 */
const MAX_REQUEST_SIZE = UPLOAD_CONFIG.maxBytes;

/**
 * 压缩图片为base64格式（用于保存到数据库）
 * @param {string} dataUrl - 原始图片的data URL
 * @param {number} width - 图片宽度
 * @param {number} height - 图片高度
 * @param {number} maxSize - 最大尺寸（默认1000px）
 * @param {number} quality - 压缩质量（默认0.7）
 * @returns {Promise<string>} 压缩后的base64 data URL
 */
export const compressImageForDatabase = (dataUrl, width, height, maxSize = MAX_DATABASE_DIMENSION, quality = DATABASE_IMAGE_QUALITY) =>
  new Promise((resolve, reject) => {
    if (!dataUrl || typeof dataUrl !== 'string') {
      console.error('compressImageForDatabase: 无效的 dataUrl', { dataUrl, type: typeof dataUrl });
      reject(new Error('无效的 dataUrl'));
      return;
    }
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) {
      console.error('compressImageForDatabase: 无法获取 canvas context');
      reject(new Error('无法创建 canvas context'));
      return;
    }
    
    const img = new window.Image();
    
    // 设置超时，避免图片加载卡住
    const timeout = setTimeout(() => {
      console.warn('compressImageForDatabase: 图片加载超时');
      resolve(dataUrl);
    }, 10000); // 10秒超时
    
    img.onload = () => {
      clearTimeout(timeout);
      
      try {
        const maxDimension = Math.max(width, height);
        const scale = maxDimension > maxSize ? maxSize / maxDimension : 1;
        const targetWidth = Math.round(width * scale);
        const targetHeight = Math.round(height * scale);
        
        if (targetWidth <= 0 || targetHeight <= 0) {
          console.warn('compressImageForDatabase: 目标尺寸无效，使用原始数据', { targetWidth, targetHeight });
          resolve(dataUrl);
          return;
        }
        
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        
        // 检测图片格式
        const mimeMatch = typeof dataUrl === 'string' ? dataUrl.match(/^data:(image\/[a-zA-Z0-9+.-]+);/) : null;
        const sourceMime = mimeMatch ? mimeMatch[1] : null;
        const shouldKeepAlpha = sourceMime && (sourceMime.includes('png') || sourceMime.includes('webp'));

        // 如果输出为 JPEG，铺白底避免透明区域变黑
        if (!shouldKeepAlpha) {
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, targetWidth, targetHeight);
        }
        
        context.drawImage(img, 0, 0, targetWidth, targetHeight);
        
        const compressedDataUrl = shouldKeepAlpha
          ? canvas.toDataURL('image/png', quality)
          : canvas.toDataURL('image/jpeg', quality);
        
        if (!compressedDataUrl || compressedDataUrl === 'data:,') {
          console.warn('compressImageForDatabase: 压缩结果无效，使用原始数据');
          resolve(dataUrl);
          return;
        }
        
        resolve(compressedDataUrl);
      } catch (error) {
        console.error('compressImageForDatabase: 压缩过程出错', error);
        resolve(dataUrl); // 出错时使用原始数据
      }
    };
    
    img.onerror = (error) => {
      clearTimeout(timeout);
      console.error('compressImageForDatabase: 图片加载失败', error);
      resolve(dataUrl); // 加载失败时使用原始数据
    };
    
    img.src = dataUrl;
  });

/**
 * 计算base64图片的大小（字节）
 * @param {string} dataUrl - base64 data URL
 * @returns {number} 图片大小（字节）
 */
const calculateBase64Size = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== 'string') return 0;
  const base64Data = dataUrl.split(',')[1] || '';
  // base64编码后的大小约为原始大小的4/3
  return Math.round((base64Data.length * 3) / 4);
};

/**
 * 确保图片大小在限制内，如果超过则进一步压缩
 * @param {string} dataUrl - 原始图片的data URL
 * @param {number} width - 图片宽度
 * @param {number} height - 图片高度
 * @param {number} maxSizeBytes - 最大大小（字节），默认 UPLOAD_CONFIG.maxBytes（50MB）
 * @param {Function} onProgress - 进度回调函数 (message: string) => void
 * @returns {Promise<string>} 压缩后的base64 data URL
 */
export const ensureImageSizeWithinLimit = async (dataUrl, width, height, maxSizeBytes = MAX_REQUEST_SIZE, onProgress = null) => {
  if (!dataUrl || typeof dataUrl !== 'string') {
    return dataUrl;
  }

  // 计算当前图片大小
  let currentSize = calculateBase64Size(dataUrl);
  
  // 如果已经在限制内，直接返回
  if (currentSize <= maxSizeBytes) {
    return dataUrl;
  }

  const originalSizeMB = (currentSize / 1024 / 1024).toFixed(2);

  
  // 显示压缩开始提示
  if (onProgress) {
    onProgress(`图片大小 ${originalSizeMB}MB 超过限制，正在压缩...`);
  }

  // 逐步降低质量和尺寸，直到满足大小要求
  let quality = 0.85;
  let maxDimension = Math.max(width, height);
  let compressedDataUrl = dataUrl;
  let attempts = 0;
  const maxAttempts = 10; // 最多尝试10次

  while (currentSize > maxSizeBytes && attempts < maxAttempts) {
    attempts++;
    
    // 如果质量已经很低，开始缩小尺寸
    if (quality <= 0.3) {
      maxDimension = Math.floor(maxDimension * 0.9); // 每次缩小10%
      quality = 0.7; // 重置质量
    } else {
      quality = Math.max(0.3, quality - 0.1); // 每次降低0.1质量
    }

    try {
      // 更新进度提示
      if (onProgress) {
        const currentSizeMB = (currentSize / 1024 / 1024).toFixed(2);
        onProgress(`正在压缩图片... (${attempts}/${maxAttempts}) 当前大小: ${currentSizeMB}MB`);
      }
      
      compressedDataUrl = await compressImageForDatabase(
        dataUrl,
        width,
        height,
        maxDimension,
        quality
      );
      
      currentSize = calculateBase64Size(compressedDataUrl);
    
    } catch (error) {
      console.error('压缩失败:', error);
      if (onProgress) {
        onProgress(null); // 清除提示
      }
      break;
    }
  }

  if (currentSize > maxSizeBytes) {
    const finalSizeMB = (currentSize / 1024 / 1024).toFixed(2);
    console.warn(`图片压缩后仍超过限制: ${finalSizeMB}MB，但已尽力压缩`);
    if (onProgress) {
      onProgress(`图片压缩完成，但大小仍为 ${finalSizeMB}MB`);
    }
  } else {
    const finalSizeMB = (currentSize / 1024 / 1024).toFixed(2);

    if (onProgress) {
      onProgress(`图片压缩完成: ${originalSizeMB}MB → ${finalSizeMB}MB`);
    }
  }

  return compressedDataUrl;
};

/**
 * 处理文件上传：压缩图片并返回元数据
 * @param {File} file - 图片文件
 * @param {{ keepOriginalDataUrl?: boolean }} [options]
 * @returns {Promise<{id: string, name: string, width: number, height: number, dataUrl: string, originalDataUrl: string|null}>}
 */
export const processImageUpload = async (file, options = {}) => {
  const { keepOriginalDataUrl = false } = options || {};
  return new Promise((resolve, reject) => {
    const formatError = validateIpeUploadFile(file);
    if (formatError) {
      reject(new Error(formatError));
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      let originalDataUrl = e.target?.result || '';
      if (!originalDataUrl) {
        reject(new Error('无法读取图片文件'));
        return;
      }

      const img = new window.Image();
      img.onload = async () => {
        try {
          // 生成图片ID（使用时间戳+随机数+索引确保唯一性）
          const imageId = `LIB-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          const imageName = file.name.replace(/\.[^/.]+$/, ''); // 移除文件扩展名
          
          // 压缩图片用于数据库存储
          const compressedDataUrl = await compressImageForDatabase(
            originalDataUrl,
            img.width,
            img.height
          );
          
          // 检查压缩结果
          if (!compressedDataUrl) {
            console.error(`图片 ${imageId} 压缩失败，compressedDataUrl 为空`);
            reject(new Error('图片压缩失败'));
            return;
          }
          
          if (typeof compressedDataUrl !== 'string') {
            console.error(`图片 ${imageId} 压缩结果类型错误:`, typeof compressedDataUrl);
            reject(new Error('图片压缩结果格式错误'));
            return;
          }

          // 计算压缩后的文件大小（base64字符串长度 * 3/4，减去data URL前缀）
          const base64Data = compressedDataUrl.split(',')[1] || '';
          const compressedSize = Math.round((base64Data.length * 3) / 4);
          
          // 计算原始文件大小后立即丢掉原图 base64，避免与压缩图双份驻留
          const originalBase64Data = originalDataUrl.split(',')[1] || '';
          const originalSize = Math.round((originalBase64Data.length * 3) / 4);
          const retainedOriginal = keepOriginalDataUrl ? originalDataUrl : null;
          originalDataUrl = null;

          resolve({
            id: imageId,
            name: imageName,
            width: img.width,
            height: img.height,
            dataUrl: compressedDataUrl, // 压缩后的base64（用于数据库）
            originalDataUrl: retainedOriginal,
            size: compressedSize, // 压缩后的文件大小（字节）
            originalSize: originalSize, // 原始文件大小（字节）
          });
        } catch (error) {
          console.error('图片处理错误:', error);
          reject(error);
        }
      };
      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = originalDataUrl;
    };
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
};

/**
 * 批量处理图片上传（B2：有界并发压缩；默认不保留原图 base64；支持 onItem 流式消费以免全量驻留）
 * @param {File[]} files - 图片文件数组
 * @param {{
 *   keepOriginalDataUrl?: boolean,
 *   onItem?: (processed: object, file: File, index: number) => (void|Promise<void>),
 *   collectResults?: boolean,
 *   concurrency?: number,
 * }} [options]
 * @returns {Promise<Array>} 处理后的图片数据数组（collectResults=false 时仅返回轻量元数据）
 */
export const processMultipleImageUploads = async (files, options = {}) => {
  const {
    keepOriginalDataUrl = false,
    onItem = null,
    collectResults = typeof onItem !== 'function',
    concurrency = UPLOAD_COMPRESS_CONCURRENCY,
  } = options || {};
  const list = Array.isArray(files) ? files : [];
  const limit = assertUploadSessionLimit(list.length, 0);
  if (!limit.ok) {
    throw new Error(limit.message);
  }
  const results = await mapWithUploadConcurrency(
    list,
    concurrency,
    async (file, index) => {
      try {
        const processed = await processImageUpload(file, { keepOriginalDataUrl });
        if (typeof onItem === 'function') {
          await onItem(processed, file, index);
        }
        if (!collectResults) {
          const light = processed
            ? {
              id: processed.id,
              name: processed.name,
              width: processed.width,
              height: processed.height,
              size: processed.size,
              originalSize: processed.originalSize,
            }
            : null;
          if (processed) {
            processed.originalDataUrl = null;
            processed.dataUrl = null;
          }
          return light;
        }
        if (processed && !keepOriginalDataUrl) {
          processed.originalDataUrl = null;
        }
        return processed;
      } catch (error) {
        console.warn('图片处理失败:', file?.name, error);
        return null;
      }
    },
  );
  return results.filter(Boolean);
};

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = src;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mime, quality);
  });
}

/**
 * 上传前压缩：最长边 2048px、JPEG 质量 0.88，透明图保留 PNG。
 * 若压缩后体积未明显减小则保留原文件。
 * 使用 Object URL 解码，避免 File→dataURL→Image 在 Network 面板刷出成百上千条 data:image。
 */
export async function compressFileForUpload(file, {
  maxDimension = UPLOAD_CONFIG.maxEdgePx,
  quality = UPLOAD_CONFIG.jpegQuality,
} = {}) {
  if (!file || !(file instanceof File)) return file;
  const formatError = validateIpeUploadFile(file);
  if (formatError) {
    throw new Error(formatError);
  }
  const normalizedInput = normalizeUploadFileName(file);
  let objectUrl = '';

  try {
    objectUrl = URL.createObjectURL(normalizedInput);
    const img = await loadImageElement(objectUrl);
    const longest = Math.max(img.width, img.height);
    const scale = longest > maxDimension ? maxDimension / longest : 1;
    const targetWidth = Math.max(1, Math.round(img.width * scale));
    const targetHeight = Math.max(1, Math.round(img.height * scale));
    const resolvedMime = await resolveUploadMime({
      blob: normalizedInput,
      fileName: normalizedInput.name,
      dataUrlHint: '',
    });
    const keepAlpha = /png|webp/i.test(resolvedMime);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return normalizedInput;

    if (!keepAlpha) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
    }
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    const outputMime = keepAlpha ? 'image/png' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, outputMime, quality);
    if (!blob) return normalizedInput;
    if (scale >= 1 && blob.size >= normalizedInput.size * 0.95) return normalizedInput;

    const baseName = (normalizedInput.name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
    const ext = keepAlpha ? '.png' : '.jpg';
    return new File([blob], `${baseName}${ext}`, { type: outputMime, lastModified: Date.now() });
  } catch {
    return normalizedInput;
  } finally {
    if (objectUrl) {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        /* ignore */
      }
    }
  }
}

export async function compressFilesForUpload(files, options) {
  const list = Array.isArray(files) ? files : [];
  return mapWithUploadConcurrency(
    list,
    UPLOAD_COMPRESS_CONCURRENCY,
    (file) => compressFileForUpload(file, options),
  );
}

/**
 * 本地选图 → 格式白名单 + canvas 强制重编码（剥离 ICC/异常尾部等），返回可直接入模板的元数据。
 * 套图模板主图/细节图入口应走此路径，避免问题原图直传导致保存失败。
 */
export async function fileToCompressedImageMeta(file) {
  const processed = await processImageUpload(file);
  const src = String(processed?.dataUrl || '').trim();
  if (!src) {
    throw new Error('图片处理失败');
  }

  const measured = await new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth || img.width || processed.width || null,
        height: img.naturalHeight || img.height || processed.height || null,
      });
    };
    img.onerror = () => {
      resolve({
        width: processed.width || null,
        height: processed.height || null,
      });
    };
    img.src = src;
  });

  return {
    id: processed.id,
    name: processed.name,
    src,
    thumbUrl: src,
    imageUrl: src,
    width: measured.width,
    height: measured.height,
    size: processed.size || null,
    _preCompressed: true,
  };
}

/**
 * 保存前对 data URL 强制重编码，兜底「入口未压缩」的历史脏数据。
 */
export async function normalizeDataUrlForPersist(dataUrl, width, height) {
  const raw = String(dataUrl || '').trim();
  if (!raw.startsWith('data:image/')) return raw;
  try {
    let compressed = await compressImageForDatabase(
      raw,
      width || MAX_DATABASE_DIMENSION,
      height || MAX_DATABASE_DIMENSION,
    );
    compressed = await ensureImageSizeWithinLimit(
      compressed,
      width || MAX_DATABASE_DIMENSION,
      height || MAX_DATABASE_DIMENSION,
    );
    return compressed || raw;
  } catch {
    return raw;
  }
}
